import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { memo, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { LiveRegion } from '@/components/ui/LiveRegion';
import { COVER_MAX_BYTES, IMAGE_TYPES, refuseFile } from '@/lib/images';

import type { HeaderImage } from './pageLabels';
import type { ImagePurpose, StagedImage } from './usePageWrites';

interface HeaderImageFieldProps {
  value: HeaderImage | null;
  onChange: (next: HeaderImage | null) => void;
  upload: (purpose: ImagePurpose, file: File) => Promise<StagedImage>;
  /** From the reply's `limits` (P3, P5). */
  altMaxLength: number;
  /** The form's own refusal of the alt text. */
  altError?: string;
  /** A save's refusal about the image: another page holds it, or it is not ours. */
  serverError?: string | null;
  disabled?: boolean;
}

const TEST_ID = 'page-editor-header-image';

/**
 * The page's optional header image (PB6, P4, P5).
 *
 * A chosen file is checked in the browser first (`refuseFile`, the shared
 * rule the logo and cover use, P40), then stored at once in the page folder,
 * so a slow or refused upload is told here. The page keeps the key only when
 * it is saved. 1600 x 900 is a hint: nothing is cropped or refused for its
 * size (the OQ14 default). Every failure stays in this panel, a server
 * failure included (P35).
 */
export const HeaderImageField = memo(function HeaderImageField({
  value,
  onChange,
  upload,
  altMaxLength,
  altError,
  serverError,
  disabled = false,
}: HeaderImageFieldProps) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function choose(file: File) {
    const refusal = refuseFile(file, COVER_MAX_BYTES);
    if (refusal) {
      setError(refusal);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const stored = await upload('header', file);
      onChange({ key: stored.key, url: stored.url, alt: value?.alt ?? '' });
    } catch (cause) {
      // An empty message is a refusal the editor already shows (a 403).
      setError(cause instanceof Error && cause.message ? cause.message : null);
    } finally {
      setBusy(false);
    }
  }

  const shown = error ?? serverError ?? null;
  const off = disabled || busy;
  const alt = value?.alt ?? '';

  return (
    <Box
      role="group"
      aria-label="Header image"
      data-testid={TEST_ID}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Typography variant="h6" component="h2">
        Header image
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 120,
          bgcolor: 'action.hover',
        }}
      >
        {value?.url ? (
          <Box
            component="img"
            src={value.url}
            alt={alt || 'Header image'}
            data-testid={`${TEST_ID}-image`}
            sx={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {value ? 'Header image chosen' : 'No header image'}
          </Typography>
        )}
      </Paper>

      {busy ? <LinearProgress aria-hidden data-testid={`${TEST_ID}-loading`} /> : null}
      <LiveRegion message={busy ? 'Sending the header image' : ''} />

      {shown ? (
        <Alert severity="error" data-testid="page-editor-header-image-error">
          {shown}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {value ? (
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={off}
              onClick={() => input.current?.click()}
              data-testid={`${TEST_ID}-replace-button`}
            >
              Replace header image
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={off}
              onClick={() => {
                setError(null);
                onChange(null);
              }}
              data-testid={`${TEST_ID}-remove-button`}
            >
              Remove header image
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={off}
            onClick={() => input.current?.click()}
            data-testid={`${TEST_ID}-choose-button`}
          >
            Choose header image
          </Button>
        )}
      </Box>

      {/* Hidden: the buttons above are its label. */}
      <input
        ref={input}
        type="file"
        accept={IMAGE_TYPES.join(',')}
        hidden
        aria-hidden
        tabIndex={-1}
        data-testid={`${TEST_ID}-file-input`}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared, so choosing the same file again after a failure still fires.
          event.target.value = '';
          if (file) void choose(file);
        }}
      />

      <Typography
        variant="caption"
        color="text.secondary"
        data-testid={`${TEST_ID}-hint`}
      >
        Recommended 1600 × 900. PNG, JPEG or WebP, up to 5 MB
      </Typography>

      {value ? (
        <Field
          label="Header image description"
          hint={`${alt.length} / ${altMaxLength}`}
          error={altError}
          testId={`${TEST_ID}-alt`}
        >
          <TextField
            value={alt}
            disabled={disabled}
            onChange={(event) => onChange({ ...value, alt: event.target.value })}
            slotProps={{ htmlInput: { maxLength: altMaxLength } }}
          />
        </Field>
      ) : null}
    </Box>
  );
});
